import type { KeyboardEvent } from 'react'
import { getThumbnailUrl } from '../../api/useAssetUrl'
import type { MentionCandidate } from '../canvas-v2/nodes/shared/mentionUtils'
import { NODE_TYPE_ICON } from '../canvas-v2/nodes/shared/nodeTypeIcons'

/** Canonical mention-chip presentation shared by editable DOM and React views. */
export const MENTION_CHIP_PRESENTATION = {
  rootClassName:
    'inline-flex items-center gap-1 rounded-md border border-chrome/25 bg-chrome/15 px-1.5 py-0.5 text-xs font-medium text-chrome align-baseline mx-0.5',
  thumbnailClassName:
    'w-3.5 h-3.5 rounded-sm object-cover flex-shrink-0 pointer-events-none',
  labelClassName: 'inline-flex items-center gap-0.5 cursor-pointer hover:underline',
  iconClassName: 'pointer-events-none flex-shrink-0 inline-flex w-3 h-3 text-chrome/70',
  fallbackIconClassName: 'pointer-events-none flex-shrink-0 text-xs font-medium',
  textClassName: 'pointer-events-none',
  removeClassName:
    'ml-0.5 text-chrome/60 transition-colors hover:text-chrome leading-none',
}

/** Keep failed thumbnails from changing the chip's layout in either renderer. */
export function hideMentionThumbnail(image: HTMLImageElement): void {
  image.style.display = 'none'
}

export interface ReadOnlyMentionChipProps {
  candidate: MentionCandidate
  projectId?: string
  testId: string
  title: string
  onActivate?: (nodeId: string) => void
}

/** Canonical mention chip for serialized/read-only content (no remove control). */
export function ReadOnlyMentionChip({
  candidate,
  projectId,
  testId,
  title,
  onActivate,
}: ReadOnlyMentionChipProps) {
  const thumbnailUrl = candidate.thumbnailPath && projectId
    ? getThumbnailUrl(projectId, candidate.thumbnailPath, 0)
    : undefined
  const icon = NODE_TYPE_ICON[candidate.type]
  const activate = () => onActivate?.(candidate.id)
  const handleKeyDown = (event: KeyboardEvent<HTMLSpanElement>) => {
    if (!onActivate || (event.key !== 'Enter' && event.key !== ' ')) return
    event.preventDefault()
    activate()
  }

  return (
    <span
      contentEditable={false}
      data-mention-pill="true"
      data-node-id={candidate.id}
      data-label={candidate.label}
      data-icon={candidate.icon}
      data-testid={testId}
      className={MENTION_CHIP_PRESENTATION.rootClassName}
      role={onActivate ? 'button' : undefined}
      tabIndex={onActivate ? 0 : undefined}
      title={title}
      onClick={activate}
      onKeyDown={handleKeyDown}
    >
      {thumbnailUrl && (
        <img
          src={thumbnailUrl}
          alt=""
          className={MENTION_CHIP_PRESENTATION.thumbnailClassName}
          onError={(event) => hideMentionThumbnail(event.currentTarget)}
        />
      )}
      <span
        data-mention-chip-label="true"
        data-testid={`mention-chip-label-${candidate.id}`}
        className={MENTION_CHIP_PRESENTATION.labelClassName}
      >
        {icon ? (
          <span className={MENTION_CHIP_PRESENTATION.iconClassName}>{icon}</span>
        ) : candidate.icon ? (
          <span className={MENTION_CHIP_PRESENTATION.fallbackIconClassName}>
            {candidate.icon}
          </span>
        ) : null}
        <span className={MENTION_CHIP_PRESENTATION.textClassName}>{candidate.label}</span>
      </span>
    </span>
  )
}
